import { useTranslation } from "react-i18next";
import { FileSpreadsheet, User } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { CampaignGroupsPopover } from "./CampaignGroupsPopover.js";
import { contactsValueChipClass } from "../contacts/contacts-table-presentation.js";
import { cn } from "../../lib/utils.js";
import type { CampaignManageSourceTab } from "./campaigns-presentation.js";

export function CampaignAudienceCurrentSources({
  contactCount,
  groups,
  csvFileName,
  canMutate,
  saving,
  onSelectTab,
  onReplaceCsv,
  onRemoveCsv,
}: {
  contactCount: number;
  groups: ReadonlyArray<{ id: number; name: string }>;
  csvFileName: string | null;
  canMutate: boolean;
  saving?: boolean;
  onSelectTab: (tab: CampaignManageSourceTab) => void;
  onReplaceCsv: () => void;
  onRemoveCsv: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const hasContacts = contactCount > 0;
  const hasGroups = groups.length > 0;
  const hasCsv = csvFileName != null && csvFileName.length > 0;

  if (!hasContacts && !hasGroups && !hasCsv) return null;

  return (
    <div className="grid gap-1.5 px-6 pb-1" data-testid="campaign-source-summary">
      <h3 className="m-0 text-[0.75rem] font-semibold text-muted">
        {t("campaigns.manage.existing")}
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {hasContacts ? (
          <button
            type="button"
            disabled={!canMutate}
            className={cn(contactsValueChipClass, "gap-1 border-line/70")}
            data-testid="campaign-source-row-contacts"
            onClick={() => onSelectTab("contacts")}
          >
            <User size={11} aria-hidden="true" />
            {t("campaigns.sources.contactsAttached", { count: contactCount })}
          </button>
        ) : null}
        {hasGroups ? (
          <CampaignGroupsPopover groups={groups} testId="campaign-source-row-groups" />
        ) : null}
        {hasCsv ? (
          <div
            className={cn(contactsValueChipClass, "max-w-full gap-1")}
            data-testid="campaign-source-row-csv"
          >
            <FileSpreadsheet size={11} aria-hidden="true" />
            <span className="min-w-0 truncate">{csvFileName}</span>
            {canMutate ? (
              <span className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 text-[0.7rem]"
                  onClick={onReplaceCsv}
                >
                  {t("campaigns.csv.replace")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 text-[0.7rem]"
                  disabled={saving}
                  onClick={onRemoveCsv}
                >
                  {t("campaigns.csv.remove")}
                </Button>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
